import React from 'react';
import { BaselineKPIData } from '../types';
import { formatSafeCurrency } from '../utils';

export interface BaselineKPITilesProps {
  data: BaselineKPIData | null;
  baselineEnabled: boolean;  // Explicitly required to ensure intentional usage
  className?: string;
}

/**
 * BaselineKPITiles - Display baseline cost analysis KPIs
 * 
 * Shows either:
 * 1. Calculated baseline costs and projections (when data is available)
 * 2. "Baseline needed" state with CTA (when baselineEnabled is false)
 * 3. "Calculation unavailable" state (when enabled but data is null/incomplete)
 */
export const BaselineKPITiles: React.FC<BaselineKPITilesProps> = ({ 
  data, 
  baselineEnabled,
  className = '' 
}) => {
  // Case 1: Baseline calculation is disabled
  if (!baselineEnabled) {
    return (
      <div className={`baseline-kpi-tiles baseline-disabled ${className}`}>
        <div className="baseline-cta-card">
          <div className="cta-icon">⚠️</div>
          <div className="cta-content">
            <h3>Baseline Needed</h3>
            <p>
              To see your current system's cost projection, we need efficiency data.
              Please provide one of the following:
            </p>
            <ul>
              <li>SEDBUK band or rating (A-G)</li>
              <li>Gas Council (GC) number from your boiler</li>
              <li>EPC band from your certificate</li>
            </ul>
            <p className="cta-hint">
              You can find this information on your boiler's rating plate or EPC certificate.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Case 2: Baseline enabled but data is missing or incomplete
  if (!data || (data.annualCost_gbp === null && data.totalCost_gbp === null)) {
    return (
      <div className={`baseline-kpi-tiles baseline-unavailable ${className}`}>
        <div className="baseline-info-card">
          <div className="info-icon">ℹ️</div>
          <div className="info-content">
            <h3>Calculation Unavailable</h3>
            <p>
              Baseline cost calculation is enabled but could not be completed with the provided data.
              This might be due to missing heat demand or system age information.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Case 3: Normal display with calculated values
  const {
    annualCost_gbp,
    totalCost_gbp,
    periodYears,
    remainingLifespan_years,
    expectedReplacements,
    efficiencySource,
    sludgeRiskApplied,
    sludgeRiskLevel,
  } = data;

  const lifespanText = remainingLifespan_years
    ? `${remainingLifespan_years.min}-${remainingLifespan_years.max} years`
    : 'N/A';

  const replacementsText = expectedReplacements !== null ? expectedReplacements : 'N/A';

  return (
    <div className={`baseline-kpi-tiles ${className}`}>
      <div className="kpi-tile">
        <div className="kpi-label">Annual Cost</div>
        <div className="kpi-value">
          {annualCost_gbp !== null ? formatSafeCurrency(annualCost_gbp) : 'N/A'}
        </div>
      </div>

      <div className="kpi-tile">
        <div className="kpi-label">Total Cost ({periodYears}y)</div>
        <div className="kpi-value">
          {totalCost_gbp !== null ? formatSafeCurrency(totalCost_gbp) : 'N/A'}
        </div>
      </div>

      <div className="kpi-tile">
        <div className="kpi-label">Remaining Lifespan</div>
        <div className="kpi-value">{lifespanText}</div>
      </div>

      <div className="kpi-tile">
        <div className="kpi-label">Expected Replacements</div>
        <div className="kpi-value">{replacementsText}</div>
      </div>

      <div className="kpi-metadata">
        <div className="metadata-item">Efficiency: {efficiencySource || 'Unknown'}</div>
        {sludgeRiskApplied && (
          <div className="metadata-item">
            Sludge Risk: {sludgeRiskLevel || 'Applied'}
          </div>
        )}
      </div>
    </div>
  );
};
